import { FormEvent, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../../../../shared/components/Button";
import useBodyScrollLock from "../../../../shared/hooks/useBodyScrollLock";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import type { ClubRequestPayload } from "../../shared/types/club";

const EMPTY_FORM: ClubRequestPayload = {
  requesterName: "",
  requesterEmail: "",
  requesterPhone: "",
  clubName: "",
  city: "",
  state: "",
  address: "",
  websiteUrl: "",
  instagramUrl: "",
  notes: "",
};

type ClubRequestModalProps = {
  open: boolean;
  saving: boolean;
  onSubmit: (payload: ClubRequestPayload) => Promise<void>;
  onClose: () => void;
};

export default function ClubRequestModal({
  open,
  saving,
  onSubmit,
  onClose,
}: ClubRequestModalProps) {
  const t = siteTexts.clubs.requestClub;
  const [form, setForm] = useState<ClubRequestPayload>(EMPTY_FORM);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open, saving]);

  function setField<K extends keyof ClubRequestPayload>(
    key: K,
    value: ClubRequestPayload[K],
  ) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await onSubmit({
        ...form,
        state: form.state.toUpperCase(),
      });
      setForm(EMPTY_FORM);
    } catch (_error) {
      return;
    }
  }

  if (!open) return null;

  return createPortal(
    <div className="club-request-modal" role="presentation">
      <button
        type="button"
        className="club-request-modal__backdrop"
        aria-label={t.close}
        onClick={() => {
          if (!saving) onClose();
        }}
      />
      <form
        className="club-request-modal__panel"
        onSubmit={handleSubmit}
        role="dialog"
        aria-modal="true"
        aria-label={t.title}
      >
        <div className="club-request-modal__header">
          <div>
            <span className="material-symbols-outlined" aria-hidden="true">
              add_location_alt
            </span>
            <div>
              <h2>{t.title}</h2>
              <p>{t.subtitle}</p>
            </div>
          </div>
          <Button
            type="button"
            variant="pill"
            onClick={onClose}
            disabled={saving}
          >
            {siteTexts.common.cancel}
          </Button>
        </div>

        <div className="club-request-modal__notice">
          <span className="material-symbols-outlined" aria-hidden="true">
            info
          </span>
          <div>
            <strong>{t.requiredHint}</strong>
            <p>{t.contactHint}</p>
          </div>
        </div>

        <div className="club-request-modal__grid">
          <label>
            <span>{t.fields.clubName}</span>
            <input
              value={form.clubName}
              placeholder={t.placeholders.clubName}
              onChange={(event) => setField("clubName", event.target.value)}
              required
            />
          </label>
          <label>
            <span>{t.fields.city}</span>
            <input
              value={form.city}
              placeholder={t.placeholders.city}
              onChange={(event) => setField("city", event.target.value)}
              required
            />
          </label>
          <label>
            <span>{t.fields.state}</span>
            <input
              value={form.state}
              placeholder={t.placeholders.state}
              maxLength={2}
              onChange={(event) =>
                setField("state", event.target.value.toUpperCase())
              }
              required
            />
          </label>
          <label>
            <span>{t.fields.requesterName}</span>
            <input
              value={form.requesterName}
              placeholder={t.placeholders.requesterName}
              onChange={(event) =>
                setField("requesterName", event.target.value)
              }
            />
          </label>
          <label>
            <span>{t.fields.requesterEmail}</span>
            <input
              type="email"
              value={form.requesterEmail}
              placeholder={t.placeholders.requesterEmail}
              onChange={(event) =>
                setField("requesterEmail", event.target.value)
              }
            />
          </label>
          <label>
            <span>{t.fields.requesterPhone}</span>
            <input
              inputMode="tel"
              value={form.requesterPhone}
              placeholder={t.placeholders.requesterPhone}
              onChange={(event) =>
                setField("requesterPhone", event.target.value)
              }
            />
          </label>
          <label className="club-request-modal__wide">
            <span>{t.fields.address}</span>
            <input
              value={form.address}
              placeholder={t.placeholders.address}
              onChange={(event) => setField("address", event.target.value)}
            />
          </label>
          <label>
            <span>{t.fields.websiteUrl}</span>
            <input
              value={form.websiteUrl}
              placeholder={t.placeholders.websiteUrl}
              onChange={(event) => setField("websiteUrl", event.target.value)}
            />
          </label>
          <label>
            <span>{t.fields.instagramUrl}</span>
            <input
              value={form.instagramUrl}
              placeholder={t.placeholders.instagramUrl}
              onChange={(event) => setField("instagramUrl", event.target.value)}
            />
          </label>
          <label className="club-request-modal__wide">
            <span>{t.fields.notes}</span>
            <textarea
              value={form.notes}
              placeholder={t.placeholders.notes}
              rows={4}
              onChange={(event) => setField("notes", event.target.value)}
            />
          </label>
        </div>

        <div className="club-request-modal__actions">
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? t.saving : t.submit}
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
